import React, { useCallback, useEffect, useState } from 'react'
import { Button, Typography } from 'antd'
import { LinkOutlined, ReloadOutlined } from '@ant-design/icons'
import { ordersApi } from 'api/orders'
import { IAdminPaymentLink } from 'models/orders'
import { showServerError } from 'shared/utils/showServerError'

// После истечения таймера ЮKassa закрывает ссылку ещё 1–2 минуты, до этого новую не создать
const CLOSING_POLL_MS = 15 * 1000

const formatTimeLeft = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

interface ISuperOrderPaymentLinkProps {
  // Любой заказ супер-заказа: платёж в ЮKassa один на всю покупку
  orderId: string
}

export const SuperOrderPaymentLink: React.FC<ISuperOrderPaymentLinkProps> = ({ orderId }) => {
  const [link, setLink] = useState<IAdminPaymentLink | null>()
  const [isLoadFailed, setIsLoadFailed] = useState(false)
  const [isRenewing, setIsRenewing] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const loadLink = useCallback(async () => {
    try {
      const { data } = await ordersApi.getPaymentLink(orderId)
      setLink(data.link)
      setIsLoadFailed(false)
    } catch {
      setIsLoadFailed(true)
    }
  }, [orderId])

  useEffect(() => {
    loadLink()
  }, [loadLink])

  const expiresAt = link?.url && link.expires_at ? new Date(link.expires_at).getTime() : null
  const timeLeft = expiresAt === null ? 0 : expiresAt - now
  const isActive = expiresAt !== null && timeLeft > 0
  const isClosing = expiresAt !== null && timeLeft <= 0

  useEffect(() => {
    if (expiresAt === null) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [expiresAt])

  useEffect(() => {
    if (!isClosing) return
    const timer = setInterval(loadLink, CLOSING_POLL_MS)
    return () => clearInterval(timer)
  }, [isClosing, loadLink])

  const renewLink = async () => {
    setIsRenewing(true)
    try {
      const { data } = await ordersApi.renewPaymentLink(orderId)
      setLink(data.link)
    } catch (e) {
      showServerError(e)
      loadLink()
    } finally {
      setIsRenewing(false)
    }
  }

  if (link === undefined && isLoadFailed) {
    return (
      <Typography.Text type='danger' style={{ display: 'block', marginTop: 15 }}>
        Не удалось проверить ссылку на оплату. Обновите страницу
      </Typography.Text>
    )
  }
  if (!link) return null

  const soldOut = link.sold_out ?? []
  const openLabel = isActive ? formatTimeLeft(timeLeft) : isClosing ? '00:00' : 'нет ссылки'
  const hint = soldOut.length > 0
    ? null
    : isActive
      ? 'Новую ссылку можно создать, когда эта закроется'
      : isClosing
        ? 'ЮKassa закрывает ссылку, новую можно создать через 1–2 минуты'
        : null

  return (
    <>
      <Button
        block
        size='middle'
        htmlType='button'
        icon={<LinkOutlined />}
        style={{ marginTop: 15 }}
        disabled={!isActive}
        onClick={() => link.url && window.open(link.url, '_blank', 'noopener,noreferrer')}
      >
        Страница оплаты ({openLabel})
      </Button>
      <Button
        block
        size='middle'
        htmlType='button'
        icon={<ReloadOutlined />}
        style={{ marginTop: 10 }}
        disabled={link.url !== null || soldOut.length > 0}
        loading={isRenewing}
        onClick={renewLink}
      >
        Создать новую ссылку
      </Button>
      {hint && (
        <Typography.Text type='secondary' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
          {hint}
        </Typography.Text>
      )}
      {soldOut.length > 0 && (
        <Typography.Text type='danger' style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
          Не хватает товаров — новую ссылку не создать:
          {soldOut.map((name) => (
            <span key={name} style={{ display: 'block' }}>{name}</span>
          ))}
        </Typography.Text>
      )}
    </>
  )
}
